import { useEffect, useId, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import type { Suggestion } from "@/lib/rota.functions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type PlaceValue = { address: string; placeId?: string };

export function PlaceInput({
  label,
  placeholder,
  value,
  onChange,
  actions,
}: {
  label: string;
  placeholder: string;
  value: PlaceValue;
  onChange: (value: PlaceValue) => void;
  /** Botões opcionais (reordenar/remover) exibidos ao lado do rótulo. */
  actions?: React.ReactNode;
}) {

  const id = useId();
  const [options, setOptions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const sessionToken = useRef(Math.random().toString(36).slice(2));
  const skipNext = useRef(false);
  // Só buscamos sugestões quando a pessoa digita: endereços vindos de um link
  // compartilhado (ou preenchidos pelo sistema) não abrem a lista sozinhos.
  const typed = useRef(false);


  useEffect(() => {
    if (!typed.current || skipNext.current) {
      skipNext.current = false;
      return;
    }

    const term = value.address.trim();
    if (term.length < 3) {
      setOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      let cancelled = false;
      import("@/lib/rota.functions")
        .then(({ suggestPlaces }) =>
          suggestPlaces({ data: { input: term, sessionToken: sessionToken.current } }),
        )
        .then((res) => {
          if (cancelled) return;
          setOptions(res.suggestions);
          setOpen(res.suggestions.length > 0);
        })
        .catch(() => setOptions([]));
      return () => {
        cancelled = true;
      };
    }, 400);
    return () => clearTimeout(timer);
  }, [value.address]);

  return (
    <div className="relative">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </Label>
        {actions ? <div className="flex items-center gap-1">{actions}</div> : null}
      </div>

      <div className="relative mt-1.5">
        <MapPin className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id={id}
          autoComplete="off"
          className="h-12 pl-9 text-base"
          placeholder={placeholder}
          value={value.address}
          onChange={(e) => {
            typed.current = true;
            onChange({ address: e.target.value });
          }}

          onFocus={() => options.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
      </div>
      {open && options.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-border bg-popover shadow-lg">
          {options.map((option) => (
            <li key={option.placeId}>
              <button
                type="button"
                className="block w-full px-3 py-2.5 text-left text-sm text-popover-foreground hover:bg-accent"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  skipNext.current = true;
                  onChange({ address: option.label, placeId: option.placeId });
                  setOpen(false);
                  sessionToken.current = Math.random().toString(36).slice(2);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
